import React, { useState } from 'react';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import { Button } from '../../Shared/ui/button';
import { Input } from '../../Shared/ui/input';
import { Card } from '../../Shared/ui/card';

interface AddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStudentAdded: () => void;
}

const AddStudentModal: React.FC<AddStudentModalProps> = ({ isOpen, onClose, onStudentAdded }) => {
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [gradeLevel, setGradeLevel] = useState<5 | 6>(5);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    if (pin.length !== 5) {
      setError('PIN must be exactly 5 digits.');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.addStudent({
        name,
        pin,
        grade_level: gradeLevel,
      });
      onStudentAdded();
      setName('');
      setPin('');
      setGradeLevel(5);
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Add Student</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label htmlFor="student-name" className="block text-sm font-medium text-gray-700 mb-2">
              Student Name
            </label>
            <Input
              id="student-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Enter student's full name"
            />
          </div>

          <div>
            <label htmlFor="grade-level" className="block text-sm font-medium text-gray-700 mb-2">
              Grade Level
            </label>
            <select
              id="grade-level"
              value={gradeLevel}
              onChange={(e) => setGradeLevel(Number(e.target.value) as 5 | 6)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value={5}>Grade 5</option>
              <option value={6}>Grade 6</option>
            </select>
          </div>

          <div>
            <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
              5-Digit PIN
              <span className="text-gray-500 text-xs ml-2">(for student login)</span>
            </label>
            <Input
              id="pin"
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
              required
              maxLength={5}
              pattern="[0-9]*"
              inputMode="numeric"
              placeholder="12345"
              className={pin.length > 0 && pin.length !== 5 ? 'border-red-300' : ''}
            />
            {pin.length > 0 && pin.length !== 5 && (
              <p className="mt-1 text-xs text-red-600">PIN must be exactly 5 digits</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Your child will use this PIN to log in with your email
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isLoading || pin.length !== 5}>
              {isLoading ? 'Adding...' : 'Add Student'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddStudentModal;
